"use client";

import React, { useEffect, useState } from "react";

const TermsconditionOrganism = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedItem, setExpandedItem] = useState("purpose");
  const [scrollProgress, setScrollProgress] = useState(0);

  const sections = [
    {
      id: "overview",
      title: "Terms & Conditions Overview",
      eyebrow: "Foundation",
      content: [
        "Terms & Conditions (T&C), also known as Terms of Service (ToS), are a legal agreement between a company and its users.",
        "They define rules, rights, and responsibilities for using the company website, products, and services.",
        "When users interact with company offerings, they are expected to follow these terms.",
      ],
    },
    {
      id: "purpose",
      title: "Purpose of Terms & Conditions",
      eyebrow: "Section 01",
      content: [
        "The primary purpose is to protect the Company's interests and define user expectations.",
        "The terms clarify rights and limitations for both users and the Company.",
        "They establish a transparent operating framework for service usage.",
      ],
    },
    {
      id: "binding",
      title: "Legally Binding Agreement",
      eyebrow: "Section 02",
      content: [
        "By accepting Terms & Conditions (for example, clicking 'I Agree'), users enter into a legally binding contract.",
        "Users are advised to read and understand all terms before acceptance.",
        "Users who do not agree may be denied access to Company services.",
      ],
    },
    {
      id: "content",
      title: "What These Terms Cover",
      eyebrow: "Section 03",
      content: [
        "Typical areas include user rights and licenses, acceptable use policy, and intellectual property rights.",
        "They may include liability limitations, privacy policy references, and refund/cancellation policy terms.",
        "They also define dispute resolution process and governing law.",
      ],
    },
    {
      id: "access-updates",
      title: "Accessibility and Updates",
      eyebrow: "Section 04",
      content: [
        "The Terms & Conditions should remain accessible, typically via footer links or account creation workflows.",
        "The Company may update terms periodically based on legal, business, or product requirements.",
        "Users should be notified of updates and may need to accept revised terms to continue using services.",
      ],
    },
    {
      id: "restrictions",
      title: "Age, Privacy, and Liability",
      eyebrow: "Section 05",
      content: [
        "Some services may apply age restrictions; minors may require parental consent.",
        "The terms often reference the Privacy Policy explaining how user data is collected, used, and protected.",
        "Liability limitation clauses may apply for damages arising from service usage, as permitted by law.",
      ],
    },
    {
      id: "disputes",
      title: "Dispute Resolution and Compliance",
      eyebrow: "Section 06",
      content: [
        "Terms usually define how disputes are resolved, which may include arbitration provisions.",
        "Class-action limitations may be included where legally valid.",
        "The Company must ensure all terms are clear, transparent, and compliant with applicable laws.",
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.35, rootMargin: "-12% 0px -45% 0px" },
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleAccordion = (id) => {
    setExpandedItem((prev) => (prev === id ? "" : id));
  };

  return (
    <div className="bg-background-primary text-text-primary font-body min-h-screen selection:bg-brand-dim selection:text-brand">
      <div className="fixed left-0 top-0 z-50 h-[3px] w-full bg-brand/10">
        <div className="h-full bg-gradient-to-r from-brand to-accent transition-all duration-200" style={{ width: `${scrollProgress}%` }} />
      </div>

      <main className="container !py-14 !md:py-20">
        <header className="relative overflow-hidden rounded-3xl border border-border-primary bg-surface-primary !px-7 !py-10 !md:px-12 !md:py-14 shadow-lg animate-fade-up">
          <div className="relative grid !gap-7 lg:grid-cols-[1.2fr_0.85fr] lg:items-end">
            <div>
              <h1 className="!mt-5 text-3xl font-display font-extrabold leading-[1.02] tracking-tight text-text-primary md:text-6xl">
                Terms & <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Conditions</span>
              </h1>
              <p className="!mt-5 max-w-3xl text-base leading-relaxed text-text-primary-2 md:text-[19px]">
                These terms define the legal relationship between The Company and users, including usage rights, responsibilities, policies, and dispute frameworks.
              </p>
            </div>
            <div className="grid !gap-3 text-sm">
              <div className="rounded-2xl border border-border-primary bg-surface-primary !p-4 shadow-sm">
                <p className="font-semibold text-text-primary">Purpose</p>
                <p className="!mt-1 text-text-secondary">Protect Company interests and set user expectations.</p>
              </div>
              <div className="rounded-2xl border border-border-primary bg-surface-primary !p-4 shadow-sm">
                <p className="font-semibold text-text-primary">Binding Nature</p>
                <p className="!mt-1 text-text-secondary">Acceptance forms a legally binding agreement.</p>
              </div>
            </div>
          </div>
        </header>

        <div className="!mt-12 grid grid-cols-1 !gap-10 lg:grid-cols-12 xl:gap-12">
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="sticky !top-24 rounded-2xl border border-border-primary bg-surface-primary !p-6 shadow-sm md:p-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-text-primary">Terms Sections</p>
              <nav className="!mt-5 grid !gap-2.5">
                {sections.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={() => setExpandedItem(section.id)}
                    className={`group flex items-center justify-between rounded-xl border !px-4 !py-3 text-sm transition-all duration-300 ${
                      activeSection === section.id
                        ? "border-border-primary bg-surface-primary text-brand"
                        : "border-transparent text-text-primary-2 hover:border-border hover:bg-bg hover:text-text-primary"
                    }`}
                  >
                    <span className="font-medium text-text-primary-2">
                      {String(index + 1).padStart(2, "0")}. {section.title}
                    </span>
                    <span className={`h-2 w-2 rounded-full ${activeSection === section.id ? "bg-brand" : "bg-border"}`} />
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <section className="!space-y-8 lg:col-span-8 xl:col-span-9">
            {sections.map((section, index) => {
              const isExpanded = expandedItem === section.id;
              return (
                <article
                  key={section.id}
                  id={section.id}
                  className={`scroll-mt-28 overflow-hidden rounded-2xl border bg-surface-primary transition-all duration-300 ${
                    activeSection === section.id ? "border-brand/30 shadow-md" : "border-border-primary shadow-sm hover:shadow-md"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleAccordion(section.id)}
                    className={`flex !w-full items-start justify-between !gap-6 !px-7 !py-7 text-left md:px-10! md:py-8! cursor-pointer ${
                      activeSection === section.id ? "bg-surface-primary" : "bg-surface-primary"
                    }`}
                    aria-expanded={isExpanded}
                    aria-controls={`panel-${section.id}`}
                  >
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
                        {section.eyebrow} / {String(index).padStart(2, "0")}
                      </p>
                      <h2 className="!mt-3 inline-flex rounded-md  px-2.5 py-1 font-display text-lg font-bold tracking-tight text-text-primary md:text-3xl">{section.title}</h2>
                    </div>
                    <div
                      className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-lg font-semibold transition-all duration-300 ${
                        isExpanded ? "border-brand/30 bg-brand-dim text-brand" : "border-border bg-bg text-text-primary-2"
                      }`}
                    >
                      {isExpanded ? "-" : "+"}
                    </div>
                  </button>

                  <div id={`panel-${section.id}`} className={`grid transition-all duration-400 ease-in-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="border-t border-border-primary bg-bg/35 px-7 pb-7 pt-6 md:px-10 md:pb-10 md:pt-7">
                        <ul className="!space-y-4">
                          {section.content.map((line, i) => (
                            <li key={`${section.id}-${i}`} className="flex !gap-3 rounded-lg !px-3 !py-2 text-text-primary-2 transition-colors duration-200 hover:bg-surface">
                              <span className="!mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/80" />
                              <span className="leading-relaxed">{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            <div className="rounded-2xl border border-border-primary bg-surface-primary !p-7 md:p-10">
              <h3 className="font-display text-2xl font-bold text-text-primary">Important Legal Note</h3>
              <p className="!mt-2 text-sm leading-relaxed text-text-primary-2 md:text-base">
                Given the legal nature of Terms & Conditions, The Company strives to keep the document clear, transparent, and compliant with relevant laws and regulations.
              </p>
              <p className="!mt-5 rounded-lg border border-border-primary bg-surface-primary !px-5 !py-4 text-sm text-text-primary-2 shadow-sm md:text-base">
                Users should review and understand the Terms & Conditions to clearly know their rights and obligations before engaging with Company services.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsconditionOrganism;
